import * as React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router";
import Costs from "../../components/Details/OverviewInfo/Costs/Costs";
import DeleteModal from "../../components/Forms/Delete/DeleteModal";
import { StatusLabel } from "../../components/Lists/Base/CompleteList/styles";
import { DetailLayoutWrapper } from "../../components/sharedStyles";
import {
  setActiveFormInstance,
  useFrequentPermissions,
  usePermissionCheck,
} from "../../store/base";
import { useInvoicingErrorPaginationCount } from "../../store/invoicingErrors/hooks/retrieve";
import { constants as editableDocConstants } from "../../store/editabledocs";
import {
  constants as invoicingConstants,
  useOtherInvoicing,
} from "../../store/invoicingOther";
import DetailPageHeaderMenu from "../Layouts/DetailPageHeaderMenu/DetailPageHeaderMenu";
import OverlaySpinner from "../../components/Loaders/OverlaySpinner";
import { useDispatch } from "react-redux";
import { ESignOrManual } from "../../components/Forms/Pickers";
import { useCanUseScrive } from "../../store/editabledocs/hooks/retrieve";
import {
  useOtherContract,
  cancelUrl,
  constants,
} from "../../store/otherContracts";
import OtherContractOverview from "./detail/Overview";
import OtherContractDocumentTabs from "./detail/Documents";
import usePermissionRedirect from "../../hooks/usePermissionRedirect";

import { toMoneyString } from "../../components/utils/stringUtils";
import ContractInvoicingBase from "../LeaseContract/Detail/ContractInvoicingBase";
import {
  contractOverviewCosts,
  getContractFromAndTo,
} from "../../components/Lists/utils/costCalculations";

export default function OtherContractDetail() {
  const storeName = constants.STORE_NAME;
  const dispatch = useDispatch();
  const { path, url } = useRouteMatch();
  const { push } = useHistory();
  const { otherContractId } = useParams();

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [cancelTypeOpen, setCancelTypeOpen] = React.useState(false);
  const [otherContract, otherContractLoading] =
    useOtherContract(otherContractId);
  const [invoicingObj] = useOtherInvoicing(otherContract?.other_invoicing?.id);
  const { hasBillectaViewPermission } = useFrequentPermissions();
  usePermissionRedirect(["view_can_contract"]);

  const canDelete = usePermissionCheck("delete_can_contract");
  const canEdit = usePermissionCheck("change_can_contract");
  const [canUseScrive] = useCanUseScrive();
  const isCancelled =
    otherContract?.closed_date || otherContract?.closed_renew_date;
  const isSigned = otherContract?.is_signed;

  // clear form states
  React.useEffect(() => {
    return () => {
      dispatch(
        setActiveFormInstance({
          storeName,
          data: {},
        })
      );
      dispatch(
        setActiveFormInstance({
          storeName: invoicingConstants.STORE_NAME,
          data: {},
        })
      );
      dispatch(
        setActiveFormInstance({
          storeName: editableDocConstants.STORE_NAME,
          data: {},
        })
      );
    };
  }, []);

  const [invoicingErrorCount] = useInvoicingErrorPaginationCount({
    filters: {
      otherinvoicing: otherContract?.other_invoicing?.id || -1,
    },
  });

  const SUB_PAGES = [
    {
      path: "/overview",
      label: "Avtal",
      component: OtherContractOverview,
    },
    {
      path: "/costs",
      label: "Debiteringsrader",
      component: Costs,
    },
    {
      path: "/docs",
      label: "Dokument",
      component: OtherContractDocumentTabs,
    },
  ];

  if (hasBillectaViewPermission) {
    SUB_PAGES.push({
      path: "/invoicing",
      label: "Avisering",
      hasError: invoicingErrorCount,
      component: ContractInvoicingBase,
    });
  }

  const subPageLinks = SUB_PAGES.map((sp) => ({
    url: `${url}${sp.path}`,
    label: sp.label,
    hasError: sp.hasError,
  }));

  const handleCancelClicked = () => {
    if (canUseScrive) {
      setCancelTypeOpen(true);
    } else {
      push(cancelUrl({ id: otherContractId, isManualSign: true }));
    }
  };

  const onCancelTypeSelected = (cancelType) => {
    push(
      cancelUrl({
        id: otherContractId,
        isManualSign: cancelType !== "digital",
      })
    );
  };

  const handleActions = [];

  if (canEdit && !isCancelled && isSigned) {
    handleActions.push({
      name: "Säg upp",
      onClick: handleCancelClicked,
    });
  }

  if (canDelete) {
    handleActions.push({
      name: "Radera",
      onClick: () => setDeleteModalOpen(true),
      isDelete: true,
    });
  }

  const renderInfoPills = () => {
    if (!otherContract) return null;
    const { contractFrom, contractTo } = getContractFromAndTo(otherContract);

    return (
      <>
        <StatusLabel state={otherContract?.state}>
          {otherContract?.state_display}
        </StatusLabel>
        {otherContract?.is_signed && (
          <StatusLabel style={{ marginLeft: 12 }} state={1}>
            Signerat
          </StatusLabel>
        )}
        <StatusLabel style={{ marginLeft: 12 }} state={4}>
          Årsintäkt ({contractFrom.format("YYYY-MM")} -{" "}
          {contractTo.format("YYYY-MM")}):{" "}
          {toMoneyString(
            contractOverviewCosts({
              costsData: invoicingObj?.cost_set,
              contract: otherContract,
            }),
            true
          )}
        </StatusLabel>
      </>
    );
  };

  return (
    <>
      <DeleteModal
        isOpen={deleteModalOpen}
        closeFunction={() => setDeleteModalOpen(false)}
        constants={constants}
        instance={otherContract}
        deletedCallback={() => push("/other-contracts")}
      />

      <ESignOrManual
        isOpen={cancelTypeOpen}
        onClose={() => setCancelTypeOpen(false)}
        onSelect={onCancelTypeSelected}
        description="Bekräfta uppsägning"
        digitalTitle="Signera digitalt"
        manualTitle="Bekräfta manuellt"
        infoText="Välj om uppsägningen ska signeras digitalt eller enbart bekräftas"
      />

      <DetailLayoutWrapper>
        {otherContractLoading && <OverlaySpinner />}
        <DetailPageHeaderMenu
          title={`Övrigt avtal ${otherContract?.id_number || "Laddar..."}`}
          breadCrumbs={[
            { url: "/other-contracts", label: "Övriga avtal" },
            { label: otherContract?.id_number || "Laddar..." },
          ]}
          eventContentType="standard.othercontract"
          eventObjectId={otherContractId}
          eventIdQueryStr="othercontract"
          {...{ subPages: subPageLinks, handleActions, renderInfoPills }}
        />

        <Switch>
          {SUB_PAGES.map((sp) => (
            <Route
              key={sp.path}
              path={`${path}${sp.path}`}
              component={sp.component}
            />
          ))}

          {/* Default sub page  */}
          <Redirect exact to={`${url}${SUB_PAGES[0].path}`} path={`${path}`} />
        </Switch>
      </DetailLayoutWrapper>
    </>
  );
}
